import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  IconButton,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Alert,
  CircularProgress,
  Link as MuiLink,
  useTheme,
  alpha,
  Fade,
  Stack,
  Snackbar,
  FormControl,
  InputLabel,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  Select,
  Chip,
  FormHelperText,
  Avatar,
  Badge,
  LinearProgress,
  Rating,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepConnector,
  stepConnectorClasses,
  styled
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Launch as LaunchIcon,
  ContentCopy as ContentCopyIcon,
  Home as HomeIcon,
  LocalMall as LocalMallIcon,
  BusinessCenter as LabelIcon,
  Language as LanguageIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  UploadFile as UploadFileIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  VideoLibrary as TikTokIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

// Stepper styling components
const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#eaeaf0',
  display: 'flex',
  height: 22,
  alignItems: 'center',
  ...(ownerState.active && {
    color: theme.palette.primary.main,
  }),
  '& .QontoStepIcon-completedIcon': {
    color: theme.palette.primary.main,
    zIndex: 1,
    fontSize: 18,
  },
  '& .QontoStepIcon-circle': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckCircleIcon className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

const StepContentBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
  borderRadius: 12,
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`
}));

// Country codes mapping
const COUNTRY_CODES = {
  'US': 'United States',
  'CA': 'Canada',
  'GB': 'United Kingdom',
  'DE': 'Germany',
  'FR': 'France',
  'JP': 'Japan',
  'CN': 'China',
  'AU': 'Australia',
  'BR': 'Brazil',
  'MX': 'Mexico',
  'IN': 'India',
  'NG': 'Nigeria',
  'ZA': 'South Africa',
  'KE': 'Kenya',
  'EG': 'Egypt'
};

const TRADEMARK_STATUSES = {
  'active': { label: 'Active', color: 'success', icon: CheckCircleIcon },
  'pending': { label: 'Pending', color: 'warning', icon: PendingIcon },
  'expired': { label: 'Expired', color: 'error', icon: WarningIcon },
  'rejected': { label: 'Rejected', color: 'error', icon: CancelIcon }
};

const SOCIAL_MEDIA_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', icon: InstagramIcon, placeholder: '@yourbrand' },
  { key: 'tiktok', label: 'TikTok', icon: TikTokIcon, placeholder: '@yourbrand' },
  { key: 'twitter', label: 'Twitter/X', icon: TwitterIcon, placeholder: '@yourbrand' },
  { key: 'facebook', label: 'Facebook', icon: FacebookIcon, placeholder: 'facebook.com/yourbrand' },
  { key: 'youtube', label: 'YouTube', icon: YouTubeIcon, placeholder: 'youtube.com/@yourbrand' },
  { key: 'linkedin', label: 'LinkedIn', icon: LinkedInIcon, placeholder: 'linkedin.com/company/yourbrand' }
];



export default function BrandsPage({ currentCompany }) {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel } = usePermissions();
  
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [companyName, setCompanyName] = useState('');
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [newBrand, setNewBrand] = useState({
    name: '',
    description: '',
    website: '',
    trademark_renewal_date: '',
    trademark_status: '',
    country_of_registration: '',
    trademark_class: '',
    auto_notify_days_before_expiry: '',
    brand_priority: 0,
    social_media_links: {},
    origin_story: ''
  });
  
  // File upload state
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [brandDocuments, setBrandDocuments] = useState({});
  const [tempFiles, setTempFiles] = useState([]);
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    brandId: null,
    brandName: ''
  });
  
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [copySnackbar, setCopySnackbar] = useState({
    open: false,
    message: ''
  });
  
  // View dialog state
  const [viewDialog, setViewDialog] = useState({
    open: false,
    brand: null
  });

  // Fetch functions - defined before useEffect to avoid hoisting issues
  const fetchBrandDocuments = async (brandId) => {
    try {
      const { data, error } = await supabase.storage
        .from('brands-trademark-documents')
        .list(brandId, { limit: 100 });

      if (!error && data) {
        setBrandDocuments(prev => ({
          ...prev,
          [brandId]: data
        }));
      }
    } catch (err) {
      // Silently fail for document fetching
    }
  };

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (data) {
        setBrands(data || []);
        // Fetch documents for each brand
        for (const brand of data) {
          await fetchBrandDocuments(brand.id);
        }
      }
    } catch (error) {
      setError('Failed to load brands. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      if (currentCompany && currentCompany.name) {
        setCompanyName(currentCompany.name);
        return;
      }

      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      if (data) {
        setCompanyName(data.name);
      }
    } catch (err) {
      setCompanyName('Company Name');
    }
  };

  const applyFilters = () => {
    if (!brands || brands.length === 0) {
      setFilteredBrands([]);
      return;
    }
    
    let filtered = [...brands];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(brand => 
        (brand.name?.toLowerCase().includes(term)) ||
        (brand.description?.toLowerCase().includes(term)) ||
        (brand.website?.toLowerCase().includes(term)) ||
        (brand.origin_story?.toLowerCase().includes(term)) ||
        (brand.trademark_class?.toLowerCase().includes(term))
      );
    }
    
    setFilteredBrands(filtered);
    setPage(0);
  };

  useEffect(() => {
    fetchBrands();
    fetchCompanyData();
  }, [companyId]);

  useEffect(() => {
    applyFilters();
  }, [brands, searchTerm]);

  // Permission check
  if (!hasPermission('products.brands.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <LabelIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Brand Directory Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Brand Directory. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }
  
  // Check edit permissions  
  const canEdit = hasPermission('products.brands.edit', 'write');







  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewBrand({ ...newBrand, [name]: value });
  };

  const handleDateChange = (name, value) => {
    setNewBrand({ ...newBrand, [name]: value ? value.format('YYYY-MM-DD') : null });
  };

  const handleSocialMediaChange = (platform, value) => {
    setNewBrand({
      ...newBrand,
      social_media_links: {
        ...newBrand.social_media_links,
        [platform]: value
      }
    });
  };

  const handleFileUpload = async (files, brandId) => {
    if (!files || files.length === 0) return;

    // For new brands, store files temporarily
    if (brandId === 'temp') {
      const newTempFiles = Array.from(files).map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type
      }));
      
      setTempFiles(prev => [...prev, ...newTempFiles]);
      setBrandDocuments(prev => ({
        ...prev,
        temp: [...(prev.temp || []), ...newTempFiles]
      }));
      setSuccess('Files selected for upload!');
      return;
    }

    setUploadingFiles(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = `${Date.now()}-${file.name}`;
        const filePath = `${brandId}/${fileName}`;

        const { error } = await supabase.storage
          .from('brands-trademark-documents')
          .upload(filePath, file);

        if (error) throw error;

        setUploadProgress(((i + 1) / files.length) * 100);
      }

      setSuccess('Documents uploaded successfully!');
      await fetchBrandDocuments(brandId);
    } catch (error) {
      setError(`Failed to upload files: ${error.message}`);
    } finally {
      setUploadingFiles(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteDocument = async (doc, brandId) => {
    try {
      // For temp files, just remove from local state
      if (brandId === 'temp') {
        setTempFiles(prev => prev.filter(file => file.name !== doc.name));
        setBrandDocuments(prev => ({
          ...prev,
          temp: prev.temp?.filter(file => file.name !== doc.name) || []
        }));
        setSuccess('Document removed!');
        return;
      }

      // For existing documents, delete from storage
      const filePath = `${brandId}/${doc.name}`;
      
      const { error: storageError } = await supabase.storage
        .from('brands-trademark-documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      setSuccess('Document deleted successfully!');
      await fetchBrandDocuments(brandId);
    } catch (error) {
      setError(`Failed to delete document: ${error.message}`);
    }
  };

  const viewBrandDocument = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('brands-trademark-documents')
        .download(filePath);

      if (error) throw error;

      const blobUrl = URL.createObjectURL(data);
      const newWindow = window.open(blobUrl, '_blank');
      
      if (!newWindow) {
        downloadBrandDocument(filePath, fileName);
      }
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('View error:', err);
      setError(`File view failed: ${err.message}`);
    }
  };

  const downloadBrandDocument = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('brands-trademark-documents')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError(`File download failed: ${err.message}`);
    }
  };

  const handleSubmit = async () => {
    if (!newBrand.name.trim()) return;

    // Check permissions before proceeding
    if (!hasPermission('products.brands.edit', 'write')) {
      setError('You do not have permission to create or edit brands.');
      return;
    }

    try {
      const brandData = {
        name: newBrand.name.trim(),
        description: newBrand.description.trim() || null,
        website: newBrand.website.trim() || null,
        trademark_renewal_date: newBrand.trademark_renewal_date || null,
        trademark_status: newBrand.trademark_status,
        country_of_registration: newBrand.country_of_registration || null,
        trademark_class: newBrand.trademark_class.trim() || null,
        auto_notify_days_before_expiry: newBrand.auto_notify_days_before_expiry,
        brand_priority: newBrand.brand_priority,
        social_media_links: newBrand.social_media_links,
        origin_story: newBrand.origin_story.trim() || null
      };

      if (editingBrand) {
        const { error } = await supabase
          .from('brands')
          .update(brandData)
          .eq('id', editingBrand.id);

        if (error) throw error;
        setSuccess('Brand updated successfully!');
      } else {
        const { data, error } = await supabase
          .from('brands')
          .insert([{
            company_id: companyId,
            ...brandData
          }])
          .select()
          .single();

        if (error) throw error;
        
        // Upload temp files for new brand
        if (tempFiles.length > 0) {
          setUploadingFiles(true);
          setUploadProgress(0);
          
          try {
            for (let i = 0; i < tempFiles.length; i++) {
              const tempFile = tempFiles[i];
              const fileName = `${Date.now()}-${tempFile.name}`;
              const filePath = `${data.id}/${fileName}`;

              const { error: uploadError } = await supabase.storage
                .from('brands-trademark-documents')
                .upload(filePath, tempFile.file);

              if (uploadError) throw uploadError;
              
              setUploadProgress(((i + 1) / tempFiles.length) * 100);
            }
            
            setSuccess('Brand and documents created successfully!');
          } catch (uploadError) {
            setError(`Brand created but failed to upload documents: ${uploadError.message}`);
          } finally {
            setUploadingFiles(false);
            setUploadProgress(0);
          }
        } else {
          setSuccess('Brand added successfully!');
        }
      }

      resetForm();
      fetchBrands();
    } catch (error) {
      setError(`Failed to save brand: ${error.message}`);
    }
  };

  const handleEditBrand = (brand) => {
    if (!brand) return;
    
    handleCloseMenu();
    
    setEditingBrand(brand);
    setNewBrand({
      name: brand.name || '',
      description: brand.description || '',
      website: brand.website || '',
      trademark_renewal_date: brand.trademark_renewal_date || '',
      trademark_status: brand.trademark_status || 'pending',
      country_of_registration: brand.country_of_registration || '',
      trademark_class: brand.trademark_class || '',
      auto_notify_days_before_expiry: brand.auto_notify_days_before_expiry || 90,
      brand_priority: brand.brand_priority || 5,
      social_media_links: brand.social_media_links || {},
      origin_story: brand.origin_story || ''
    });
    
    setTimeout(() => {
      setShowForm(true);
    }, 50);
  };

  const handleDeleteBrand = async (brand) => {
    setDeleteDialog({
      open: true,
      brandId: brand.id,
      brandName: brand.name
    });
    handleCloseMenu();
  };

  const confirmDeleteBrand = async () => {
    // Check permissions before proceeding
    if (!hasPermission('products.brands.delete', 'delete')) {
      setError('You do not have permission to delete brands.');
      setDeleteDialog({ open: false, brandId: null, brandName: '' });
      return;
    }

    try {
      // Delete all brand documents from storage first
      const documents = brandDocuments[deleteDialog.brandId] || [];
      if (documents.length > 0) {
        const filePaths = documents.map(doc => `${deleteDialog.brandId}/${doc.name}`);
        const { error: storageError } = await supabase.storage
          .from('brands-trademark-documents')
          .remove(filePaths);

        if (storageError) {
          console.error('Error deleting brand documents:', storageError);
        }
      }

      // Also delete the entire folder if it exists
      try {
        const { data: folderFiles } = await supabase.storage
          .from('brands-trademark-documents')
          .list(deleteDialog.brandId, { limit: 1000 });

        if (folderFiles && folderFiles.length > 0) {
          const allFilePaths = folderFiles.map(file => `${deleteDialog.brandId}/${file.name}`);
          await supabase.storage
            .from('brands-trademark-documents')
            .remove(allFilePaths);
        }
      } catch (folderError) {
        console.error('Error cleaning up brand folder:', folderError);
      }

      // Delete brand record
      const { error } = await supabase
        .from('brands')
        .delete()
        .eq('id', deleteDialog.brandId);

      if (error) throw error;

      setSuccess('Brand and all documents deleted successfully!');
      setDeleteDialog({ open: false, brandId: null, brandName: '' });
      fetchBrands();
    } catch (error) {
      setError(`Failed to delete brand: ${error.message}`);
    } finally {
      setDeleteDialog({ open: false, brandId: null, brandName: '' });
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingBrand(null);
    setActiveStep(0);
    setTempFiles([]);
    setBrandDocuments(prev => {
      const { temp, ...rest } = prev;
      return rest;
    });
    setNewBrand({
      name: '',
      description: '',
      website: '',
      trademark_renewal_date: '',
      trademark_status: '',
      country_of_registration: '',
      trademark_class: '',
             auto_notify_days_before_expiry: '',
       brand_priority: 0,
       social_media_links: {},
      origin_story: ''
    });
  };

  // Step validation and navigation
  const isStepValid = (step) => {
    const isValidString = (value) => value && typeof value === 'string' && value.trim().length > 0;
    const isValidNumber = (value) => value && (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value.trim()))));
    
    switch (step) {
      case 0: // Basic Information
        return isValidString(newBrand.name) &&
               newBrand.brand_priority > 0 &&
               isValidString(newBrand.website) &&
               isValidString(newBrand.description);
      case 1: // Trademark Info
        return isValidString(newBrand.trademark_status) &&
               isValidString(newBrand.country_of_registration) &&
               isValidString(newBrand.trademark_class) &&
               newBrand.trademark_renewal_date &&
               isValidNumber(newBrand.auto_notify_days_before_expiry) &&
               newBrand.auto_notify_days_before_expiry > 0;
      case 2: // Social & Story
        return isValidString(newBrand.origin_story);
      case 3: // Documents
        return true; // Documents are optional
      default:
        return false;
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  // Step render functions
  const renderBasicInfoStep = () => (
    <StepContentBox>
      <Typography variant="h6" sx={{ 
        fontWeight: 500, 
        mb: 3, 
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <LabelIcon />
        Basic Brand Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel 
              htmlFor="brand-name-input"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}
            >
              Brand Name *
            </InputLabel>
            <OutlinedInput
              id="brand-name-input"
              fullWidth
              name="name"
              value={newBrand.name}
              onChange={handleChange}
              required
              autoFocus
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box>
            <Typography component="legend" variant="subtitle2" color={newBrand.brand_priority === 0 ? 'error' : 'text.secondary'} gutterBottom sx={{ fontWeight: 500, fontSize: '1rem' }}>
              Brand Priority *
            </Typography>
            <Rating
              name="brand_priority"
              value={newBrand.brand_priority}
              onChange={(event, newValue) => {
                setNewBrand({ ...newBrand, brand_priority: newValue || 0 });
              }}
              size="large"
              sx={{ 
                mt: 1,
                '& .MuiRating-icon': {
                  color: newBrand.brand_priority === 0 ? 'error.main' : undefined
                }
              }}
            />
            <FormHelperText sx={{ mt: 1, color: newBrand.brand_priority === 0 ? 'error.main' : 'text.secondary' }}>
              Rate the importance of this brand (1-5 stars) - Required
            </FormHelperText>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <FormControl fullWidth error={!newBrand.website?.trim()}>
            <InputLabel 
              htmlFor="website-input"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}
            >
              Website *
            </InputLabel>
            <OutlinedInput
              id="website-input"
              fullWidth
              name="website"
              value={newBrand.website}
              onChange={handleChange}
              required
              placeholder="e.g. www.yourbrand.com"
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <FormControl fullWidth error={!newBrand.description?.trim()}>
            <InputLabel 
              htmlFor="description-input"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}
            >
              Description *
            </InputLabel>
            <OutlinedInput
              id="description-input"
              fullWidth
              name="description"
              value={newBrand.description}
              onChange={handleChange}
              required
              multiline
              rows={3}
              placeholder="Brief description of your brand"
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            />
          </FormControl>
        </Grid>
      </Grid>
    </StepContentBox>
  );

  const renderTrademarkStep = () => (
    <StepContentBox>
      <Typography variant="h6" sx={{ 
        fontWeight: 500, 
        mb: 3, 
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <GavelIcon />
        Trademark Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!newBrand.trademark_status}>
            <InputLabel 
              htmlFor="trademark-status-input"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}
            >
              Trademark Status *
            </InputLabel>
            <Select
              labelId="trademark-status-label"
              id="trademark-status-input"
              value={newBrand.trademark_status}
              label="Trademark Status"
              name="trademark_status"
              onChange={handleChange}
              required
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            >
              <MenuItem value="">
                <em>Select status</em>
              </MenuItem>
              {Object.entries(TRADEMARK_STATUSES).map(([key, value]) => {
                const IconComponent = value.icon;
                return (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconComponent sx={{ fontSize: 18 }} />
                      {value.label}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!newBrand.country_of_registration}>
            <InputLabel 
              htmlFor="country-input"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}
            >
              Country of Registration *
            </InputLabel>
            <Select
              labelId="country-label"
              id="country-input"
              value={newBrand.country_of_registration}
              label="Country of Registration"
              name="country_of_registration"
              onChange={handleChange}
              required
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            >
              <MenuItem value="">
                <em>Select country</em>
              </MenuItem>
              {Object.entries(COUNTRY_CODES).map(([code, name]) => (
                <MenuItem key={code} value={code}>
                  {code} - {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!newBrand.trademark_class?.trim()}>
            <InputLabel 
              htmlFor="trademark-class-input"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}
            >
              Trademark Class *
            </InputLabel>
            <OutlinedInput
              id="trademark-class-input"
              fullWidth
              name="trademark_class"
              value={newBrand.trademark_class}
              onChange={handleChange}
              required
              placeholder="e.g. Class 3 - Cosmetics"
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Trademark Renewal Date *"
              value={newBrand.trademark_renewal_date ? dayjs(newBrand.trademark_renewal_date) : null}
              onChange={(newValue) => handleDateChange('trademark_renewal_date', newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette.primary.main}`
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      fontSize: '1rem'
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth error={!newBrand.auto_notify_days_before_expiry || newBrand.auto_notify_days_before_expiry <= 0}>
            <InputLabel 
              htmlFor="notify-days-input"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}
            >
              Notify Days Before Expiry *
            </InputLabel>
            <OutlinedInput
              id="notify-days-input"
              fullWidth
              name="auto_notify_days_before_expiry"
              type="number"
              value={newBrand.auto_notify_days_before_expiry}
              onChange={handleChange}
              required
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            />
            {(!newBrand.auto_notify_days_before_expiry || newBrand.auto_notify_days_before_expiry <= 0) && (
              <FormHelperText sx={{ color: 'error.main', mt: 1 }}>
                Please enter a number greater than 0
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </StepContentBox>
  );

  const renderSocialStoryStep = () => (
    <StepContentBox>
      <Typography variant="h6" sx={{ 
        fontWeight: 500, 
        mb: 3, 
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <DescriptionIcon />
        Social Media & Brand Story
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
            Origin Story
          </Typography>
          <FormControl fullWidth error={!newBrand.origin_story?.trim()} sx={{ mt: 2 }}>
            <InputLabel 
              htmlFor="origin-story-input"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}
            >
              Brand Origin Story *
            </InputLabel>
            <OutlinedInput
              id="origin-story-input"
              fullWidth
              name="origin_story"
              value={newBrand.origin_story}
              onChange={handleChange}
              required
              multiline
              rows={4}
              placeholder="Tell the story of how your brand came to be, your mission, values, and what makes it unique..."
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
            Social Media Links
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {SOCIAL_MEDIA_PLATFORMS.map((platform) => {
              const IconComponent = platform.icon;
              return (
                <Grid item xs={12} sm={6} key={platform.key}>
                  <FormControl fullWidth>
                    <InputLabel 
                      htmlFor={`social-${platform.key}-input`}
                      sx={{
                        fontWeight: 500,
                        fontSize: '1rem',
                        transform: 'translate(14px, 1px) scale(1)',
                        '&.Mui-focused, &.MuiFormLabel-filled': {
                          transform: 'translate(14px, -17px) scale(0.75)'
                        }
                      }}
                    >
                      {platform.label}
                    </InputLabel>
                    <OutlinedInput
                      id={`social-${platform.key}-input`}
                      fullWidth
                      value={newBrand.social_media_links[platform.key] || ''}
                      onChange={(e) => handleSocialMediaChange(platform.key, e.target.value)}
                      placeholder={platform.placeholder}
                      sx={{
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: `1px solid ${theme.palette.primary.main}`
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </StepContentBox>
  );

  const renderDocumentsStep = () => (
    <StepContentBox>
      <Typography variant="h6" sx={{ 
        fontWeight: 500, 
        mb: 3, 
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <UploadFileIcon />
        Trademark Documents
      </Typography>
      
      {/* File Upload */}
      <Paper 
        sx={{ 
          p: 4, 
          mb: 3, 
          border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
          borderRadius: 3,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            border: `2px dashed ${alpha(theme.palette.primary.main, 0.6)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.05)
          }
        }}
        onClick={() => document.getElementById('file-upload-input').click()}
      >
        <input
          id="file-upload-input"
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.png,.svg"
          style={{ display: 'none' }}
          onChange={(e) => handleFileUpload(e.target.files, editingBrand?.id || 'temp')}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, color: 'primary.main' }}>
          Upload Documents
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click or drag files here to upload trademark documents, certificates, logos, etc.
        </Typography>
        {uploadingFiles && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ borderRadius: 2, height: 8 }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Uploading... {Math.round(uploadProgress)}%
            </Typography>
          </Box>
        )}
      </Paper>
      
      {/* Existing Documents */}
      {((editingBrand && brandDocuments[editingBrand.id]) || brandDocuments['temp']) && 
       ((editingBrand && brandDocuments[editingBrand.id]?.length > 0) || brandDocuments['temp']?.length > 0) && (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            {editingBrand ? 'Existing Documents' : 'Selected Documents'} ({editingBrand ? brandDocuments[editingBrand.id]?.length : brandDocuments['temp']?.length})
          </Typography>
          <Grid container spacing={2}>
            {(editingBrand ? brandDocuments[editingBrand.id] : brandDocuments['temp'])?.map((doc) => (
              <Grid item xs={12} sm={6} md={4} key={doc.name}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFileIcon sx={{ color: 'primary.main' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {doc.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {editingBrand ? new Date(doc.created_at).toLocaleDateString() : 'Ready to upload'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {editingBrand && doc.path && (
                        <Tooltip title="View Document">
                          <IconButton 
                            size="small"
                            onClick={() => viewBrandDocument(doc.path, doc.name)}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { 
                                bgcolor: alpha(theme.palette.primary.main, 0.1) 
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Document">
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteDocument(doc, editingBrand?.id || 'temp')}
                          sx={{ 
                            color: 'error.main',
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.error.main, 0.1) 
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </StepContentBox>
  );

  const toggleDescription = (brandId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [brandId]: !prev[brandId]
    }));
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySnackbar({
        open: true,
        message: `${type} copied to clipboard!`
      });
    }).catch(err => {
      setError('Failed to copy to clipboard');
    });
  };

  const formatWebsiteUrl = (url) => {
    if (!url) return null;
    
    let formattedUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      formattedUrl = `https://${url}`;
    }
    
    return formattedUrl;
  };

  const getDaysUntilExpiry = (renewalDate) => {
    if (!renewalDate) return null;
    const today = new Date();
    const renewal = new Date(renewalDate);
    const diffTime = renewal - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryAlert = (brand) => {
    if (!brand.trademark_renewal_date || brand.trademark_status !== 'active') return null;
    
    const daysUntil = getDaysUntilExpiry(brand.trademark_renewal_date);
    if (daysUntil === null) return null;
    
    if (daysUntil <= 0) {
      return { severity: 'error', message: 'Expired', days: daysUntil };
    } else if (daysUntil <= (brand.auto_notify_days_before_expiry || 90)) {
      return { severity: 'warning', message: `${daysUntil} days left`, days: daysUntil };
    }
    
    return null;
  };

  const renderPriorityStars = (priority) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Rating value={priority || 0} readOnly size="small" />
        <Typography variant="caption" sx={{ ml: 1, fontWeight: 500 }}>
          {priority}/5
        </Typography>
      </Box>
    );
  };

  // Menu handlers
  const handleOpenMenu = (event, brand) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedBrand(brand);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  // Table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // View dialog handlers
  const handleViewBrand = (brand) => {
    setViewDialog({
      open: true,
      brand: brand
    });
  };

  const handleCloseViewDialog = () => {
    setViewDialog({
      open: false,
      brand: null
    });
  };

  return (
    <Fade in timeout={800}>
      <Box>
        <Container 
          maxWidth="xl"
          sx={{
            py: 3,
            pb: 8,
            px: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100vw !important'
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            {/* Enhanced Page Header */}
            <Card sx={{ 
              borderRadius: 3,
              mb: 3,
              mt: 5,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: theme.palette.grey[100],
              backdropFilter: 'blur(20px)',
              borderTop: '3px solid #FF9800',
              boxShadow: 'none',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha('#FF9800', 0.08),
                  }}>
                    <LabelIcon sx={{ fontSize: 32, color: '#FF9800' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{
                      fontWeight: 500,
                      color: '#FF9800',
                      mb: 1,
                      fontSize: '2rem',
                      letterSpacing: '-0.5px'
                    }}>
                      Brand Management
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      {companyName || 'Loading...'}
                    </Typography>
                  </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    startIcon={<RefreshIcon />}
                    onClick={fetchBrands}
                    variant="outlined"
                    sx={{ 
                      borderRadius: 2,
                      borderColor: 'grey.400',
                      color: 'primary.main',
                      borderWidth: 1,
                      fontWeight: 500,
                      px: 3,
                      py: 1.5,
                      background: 'white',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      '&:hover': {
                        borderColor: 'grey.600',
                        backgroundColor: 'white',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  {canEdit && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setShowForm(true)}
                      sx={{
                        borderRadius: 2,
                        borderColor: 'grey.400',
                        color: 'primary.main',
                        borderWidth: 1,
                        fontWeight: 500,
                        px: 3,
                        py: 1.5,
                        background: 'white',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        '&:hover': {
                          borderColor: 'grey.600',
                          backgroundColor: 'white',
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                        }
                      }}
                    >
                      New Brand
                    </Button>
                  )}
                </Box>
              </Box>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary', 
                  maxWidth: 700, 
                  lineHeight: 1.5,
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}>
                  Create and manage your product brands with comprehensive trademark tracking, document management, and social media integration.
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Search Bar */}
          <Paper 
            sx={{ 
              p: 4, 
              mb: 4, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideInUp': {
                '0%': {
                  transform: 'translateY(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateY(0)',
                  opacity: 1,
                }
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 500,
                color: 'primary.main',
                fontSize: '1.25rem'
              }}
            >
              Search Brands
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by brand name, description, origin story, or trademark class..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none'
                      },
                      '&:hover fieldset': {
                        border: 'none'
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {filteredBrands.length} brand{filteredBrands.length !== 1 && 's'} found
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Main Content */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress size={60} thickness={4} />
            </Box>
          ) : filteredBrands.length === 0 ? (
            <Paper 
              sx={{ 
                p: 8, 
                textAlign: 'center',
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`
              }}
            >
              <LabelIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.7 }} />
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'primary.main', 
                mb: 2,
                fontSize: '2.5rem',
                letterSpacing: '-0.5px'
              }}>
                {brands.length === 0 ? 'No brands yet' : 'No matching brands'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}>
                {brands.length === 0 
                  ? 'Create your first brand to start organizing and managing your product portfolio with trademark tracking'
                  : 'Try adjusting your search criteria'}
              </Typography>
              {brands.length === 0 && canEdit && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowForm(true)}
                  sx={{
                    borderRadius: 2,
                    borderColor: 'grey.400',
                    color: 'primary.main',
                    borderWidth: 1,
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    background: 'white',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      borderColor: 'grey.600',
                      backgroundColor: 'white',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                    }
                  }}
                >
                  Create First Brand
                </Button>
              )}
            </Paper>
          ) : (
            /* Table */
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideInUp': {
                '0%': {
                  transform: 'translateY(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateY(0)',
                  opacity: 1,
                }
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Brand Name
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Trademark Status
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Priority
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Website
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Documents
                      </TableCell>
                      <TableCell 
                        align="center"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3,
                          width: 160
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rowsPerPage > 0
                      ? filteredBrands.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : filteredBrands
                    ).map((brand) => {
                      const StatusIcon = TRADEMARK_STATUSES[brand.trademark_status]?.icon || PendingIcon;
                      const expiryAlert = getExpiryAlert(brand);
                      
                      return (
                        <TableRow 
                          key={brand.id} 
                          hover
                          onClick={() => handleViewBrand(brand)}
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2.5,
                              px: 3
                            }
                          }}
                        >
                          {/* Brand Name */}
                          <TableCell>
                            <Box>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: 'text.primary',
                                  fontSize: '0.95rem',
                                  mb: 0.5
                                }}
                              >
                                {brand.name}
                              </Typography>
                              {brand.description && (
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {brand.description}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          
                          {/* Trademark Status */}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                icon={<StatusIcon />}
                                label={TRADEMARK_STATUSES[brand.trademark_status]?.label || 'Pending'}
                                color={TRADEMARK_STATUSES[brand.trademark_status]?.color || 'default'}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                              />
                              {expiryAlert && (
                                <Chip
                                  icon={<WarningIcon />}
                                  label={expiryAlert.message}
                                  color={expiryAlert.severity}
                                  size="small"
                                  sx={{ fontWeight: 500 }}
                                />
                              )}
                            </Box>
                            {brand.trademark_class && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block',
                                  color: 'text.secondary',
                                  mt: 0.5
                                }}
                              >
                                Class: {brand.trademark_class}
                              </Typography>
                            )}
                          </TableCell>
                          
                          {/* Priority */}
                          <TableCell>
                            {renderPriorityStars(brand.brand_priority || 3)}
                          </TableCell>
                          
                          {/* Website */}
                          <TableCell>
                            {brand.website ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MuiLink 
                                  href={formatWebsiteUrl(brand.website)} 
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    fontSize: '0.9rem',
                                    color: 'primary.main',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline'
                                    }
                                  }}
                                >
                                  {brand.website}
                                  <LaunchIcon sx={{ fontSize: 14 }} />
                                </MuiLink>
                                <Tooltip title="Copy website URL">
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      copyToClipboard(brand.website, 'Website URL');
                                    }}
                                    sx={{ 
                                      color: 'primary.main',
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        transform: 'scale(1.1)'
                                      }
                                    }}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontStyle: 'italic',
                                  color: 'text.disabled',
                                  fontSize: '0.9rem'
                                }}
                              >
                                No website
                              </Typography>
                            )}
                          </TableCell>
                          
                          {/* Documents */}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                icon={<AttachFileIcon />}
                                label={`${(brandDocuments[brand.id] || []).length} files`}
                                size="small"
                                variant="outlined"
                                color={(brandDocuments[brand.id] || []).length > 0 ? 'primary' : 'default'}
                                sx={{ fontWeight: 500 }}
                              />
                            </Box>
                          </TableCell>
                          
                          {/* Actions */}
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                              {canEdit && (
                                <Tooltip title="Edit Brand">
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleEditBrand(brand);
                                    }}
                                    sx={{
                                      color: 'info.main',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      borderRadius: 2,
                                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                      '&:hover': {
                                        bgcolor: 'info.main',
                                        color: 'white',
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`
                                      }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              <Tooltip title={hasPermission('products.brands.delete', 'delete') ? "More options" : "More options (limited permissions)"}>
                                <span>
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleOpenMenu(e, brand);
                                    }}
                                    disabled={!hasPermission('products.brands.delete', 'delete')}
                                    sx={{
                                      color: hasPermission('products.brands.delete', 'delete') ? 'text.primary' : 'text.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      borderRadius: 2,
                                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                      '&:hover': hasPermission('products.brands.delete', 'delete') ? {
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                      } : {},
                                      '&.Mui-disabled': {
                                        color: 'text.disabled',
                                        border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`
                                      }
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderRadius: '0 0 12px 12px'
              }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredBrands.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ 
                    px: 3,
                    py: 2,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    },
                    '& .MuiTablePagination-select': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      background: 'white',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        margin: '0 2px',
                        background: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: theme.palette.primary.main,
                          color: 'white',
                          transform: 'translateY(-1px)',
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        },
                        '&.Mui-disabled': {
                          background: alpha(theme.palette.grey[300], 0.5),
                          color: theme.palette.grey[500],
                          border: `1px solid ${theme.palette.grey[300]}`,
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          )}
          
          {/* Form Dialog */}
          <Dialog 
            open={showForm} 
            onClose={resetForm} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                minHeight: '70vh'
              }
            }}
          >
            <DialogTitle sx={{ 
              pb: 2,
              pt: 3,
              px: 4,
              fontWeight: 500,
              fontSize: '1.5rem',
              color: 'primary.main'
            }}>
              {editingBrand ? 'Edit Brand' : 'Add New Brand'}
            </DialogTitle>
            <DialogContent sx={{ 
              px: 0,
              py: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})` 
            }}>
              <Box sx={{ px: 3, pt: 3 }}>
                <Stepper activeStep={activeStep} orientation="vertical" connector={<QontoConnector />}>
                  <Step>
                    <StepLabel StepIconComponent={QontoStepIcon} sx={{ 
                      '& .MuiStepLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: activeStep === 0 ? 500 : 400,
                        color: activeStep === 0 ? 'primary.main' : 'text.secondary'
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LabelIcon sx={{ fontSize: 20 }} />
                        Basic Information
                      </Box>
                    </StepLabel>
                    <StepContent>
                      <Box sx={{ mt: 2, mb: 3 }}>
                        {renderBasicInfoStep()}
                        
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="contained"
                            onClick={() => setActiveStep(1)}
                            disabled={!isStepValid(0)}
                            sx={{ borderRadius: 2 }}
                          >
                            Next: Trademark Info
                          </Button>
                    </Box>
                      </Box>
                    </StepContent>
                  </Step>
                  
                  <Step>
                    <StepLabel StepIconComponent={QontoStepIcon} sx={{ 
                      '& .MuiStepLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: activeStep === 1 ? 500 : 400,
                        color: activeStep === 1 ? 'primary.main' : 'text.secondary'
                      }
                    }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GavelIcon sx={{ fontSize: 20 }} />
                        Trademark Info
                              </Box>
                    </StepLabel>
                    <StepContent>
                      <Box sx={{ mt: 2, mb: 3 }}>
                        {renderTrademarkStep()}
                        
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                          <Button
                            onClick={() => setActiveStep(0)}
                            sx={{ borderRadius: 2 }}
                          >
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => setActiveStep(2)}
                            disabled={!isStepValid(1)}
                            sx={{ borderRadius: 2 }}
                          >
                            Next: Social & Story
                          </Button>
                        </Box>
                      </Box>
                    </StepContent>
                  </Step>
                  
                  <Step>
                    <StepLabel StepIconComponent={QontoStepIcon} sx={{ 
                      '& .MuiStepLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: activeStep === 2 ? 500 : 400,
                        color: activeStep === 2 ? 'primary.main' : 'text.secondary'
                      }
                    }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon sx={{ fontSize: 20 }} />
                        Social & Story
                              </Box>
                    </StepLabel>
                    <StepContent>
                      <Box sx={{ mt: 2, mb: 3 }}>
                        {renderSocialStoryStep()}
                        
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                          <Button
                            onClick={() => setActiveStep(1)}
                            sx={{ borderRadius: 2 }}
                          >
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => setActiveStep(3)}
                            disabled={!isStepValid(2)}
                            sx={{ borderRadius: 2 }}
                          >
                            Next: Documents
                          </Button>
                        </Box>
                      </Box>
                    </StepContent>
                  </Step>
                  
                  <Step>
                    <StepLabel StepIconComponent={QontoStepIcon} sx={{ 
                      '& .MuiStepLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: activeStep === 3 ? 500 : 400,
                        color: activeStep === 3 ? 'primary.main' : 'text.secondary'
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UploadFileIcon sx={{ fontSize: 20 }} />
                        Documents
                        </Box>
                    </StepLabel>
                    <StepContent>
                      <Box sx={{ mt: 2, mb: 3 }}>
                        {renderDocumentsStep()}
                        
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                          <Button
                            onClick={() => setActiveStep(2)}
                            sx={{ borderRadius: 2 }}
                          >
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={handleSubmit}
                            disabled={!isStepValid(0) || !isStepValid(1) || !isStepValid(2)}
                            sx={{ borderRadius: 2 }}
                          >
                            {editingBrand ? 'Update Brand' : 'Create Brand'}
                          </Button>
                                  </Box>
                                </Box>
                    </StepContent>
                  </Step>
                                                  </Stepper>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 4, pb: 3, pt: 2, gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={resetForm}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSubmit}
                disabled={!newBrand.name.trim()}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                  },
                  '&:disabled': {
                    opacity: 0.6,
                    transform: 'none'
                  }
                }}
              >
                {editingBrand ? 'Update Brand' : 'Add Brand'}
              </Button>
            </DialogActions>
          </Dialog>
          
          {/* Menu */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleCloseMenu}
            PaperProps={{
              sx: {
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
                minWidth: 180
              }
            }}
          >
            {canEdit && (
              <MenuItem onClick={() => {
                if (selectedBrand) {
                  handleEditBrand(selectedBrand);
                }
              }} sx={{ 
                py: 1.5, 
                px: 2,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08)
                }
              }}>
                <ListItemIcon>
                  <EditIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText sx={{ '& .MuiTypography-root': { fontWeight: 500 } }}>Edit Brand</ListItemText>
              </MenuItem>
            )}
            
            {hasPermission('products.brands.delete', 'delete') && (
              <>
                <Divider sx={{ borderColor: alpha(theme.palette.grey[400], 0.8) }} />
                
                <MenuItem onClick={() => {
                  if (selectedBrand) {
                    handleDeleteBrand(selectedBrand);
                  }
                }} sx={{ 
                  py: 1.5, 
                  px: 2,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.error.main, 0.08)
                  }
                }}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main', '& .MuiTypography-root': { fontWeight: 500 } }}>Delete</ListItemText>
                </MenuItem>
              </>
            )}
          </Menu>
          
          {/* Delete Dialog */}
          <Dialog 
            open={deleteDialog.open} 
            onClose={() => setDeleteDialog({ open: false, brandId: null, brandName: '' })}
            PaperProps={{
              sx: {
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                boxShadow: `0 12px 32px ${alpha(theme.palette.error.main, 0.15)}`
              }
            }}
          >
            <DialogTitle sx={{ 
              pt: 4, 
              px: 4,
              fontWeight: 500,
              fontSize: '1.25rem',
              color: 'error.main'
            }}>
              Confirm Deletion
            </DialogTitle>
            <DialogContent sx={{ 
              px: 0,
              py: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})` 
            }}>
              <Box sx={{ p: 4, background: 'white', m: 2, borderRadius: 3 }}>
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Are you sure you want to delete the brand <strong>{deleteDialog.brandName}</strong>?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This action cannot be undone. All associated documents and trademark information will be permanently deleted.
                </Typography>
              </Alert>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 4, pb: 3, pt: 2, gap: 2 }}>
              <Button 
                variant="outlined"
                onClick={() => setDeleteDialog({ open: false, brandId: null, brandName: '' })}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                color="error" 
                variant="contained" 
                onClick={confirmDeleteBrand}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.error.main, 0.25)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.35)}`
                  }
                }}
              >
                Delete Brand
              </Button>
            </DialogActions>
          </Dialog>
          
          {/* Copy Snackbar */}
          <Snackbar
            open={copySnackbar.open}
            autoHideDuration={3000}
            onClose={() => setCopySnackbar({ open: false, message: '' })}
            message={copySnackbar.message}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />

          {/* View Brand Dialog */}
          <Dialog 
            open={viewDialog.open} 
            onClose={handleCloseViewDialog} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                minHeight: '80vh'
              }
            }}
          >
            <DialogTitle sx={{ 
              pb: 2,
              pt: 3,
              px: 4,
              fontWeight: 500,
              fontSize: '1.5rem',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <LabelIcon sx={{ fontSize: 28 }} />
              {viewDialog.brand?.name || 'Brand Details'}
            </DialogTitle>
            <DialogContent sx={{ 
              px: 0,
              py: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})` 
            }}>
              <Box sx={{ p: 4, background: 'white', m: 2, borderRadius: 3 }}>
              {viewDialog.brand && (
                <Box>
                  {/* Basic Information */}
                  <Paper sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3, 
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LabelIcon /> Basic Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Brand Name</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{viewDialog.brand.name}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Priority</Typography>
                        {renderPriorityStars(viewDialog.brand.brand_priority || 3)}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewDialog.brand.description || 'No description provided'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Website</Typography>
                        {viewDialog.brand.website ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MuiLink 
                              href={formatWebsiteUrl(viewDialog.brand.website)} 
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                fontSize: '1rem',
                                color: 'primary.main',
                                fontWeight: 500,
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {viewDialog.brand.website}
                              <LaunchIcon sx={{ fontSize: 16 }} />
                            </MuiLink>
                          </Box>
                        ) : (
                          <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                            No website provided
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Trademark Information */}
                  <Paper sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3, 
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GavelIcon /> Trademark Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Status</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {(() => {
                            const StatusIcon = TRADEMARK_STATUSES[viewDialog.brand.trademark_status]?.icon || PendingIcon;
                            return (
                              <Chip
                                icon={<StatusIcon />}
                                label={TRADEMARK_STATUSES[viewDialog.brand.trademark_status]?.label || 'Pending'}
                                color={TRADEMARK_STATUSES[viewDialog.brand.trademark_status]?.color || 'default'}
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                              />
                            );
                          })()}
                          {(() => {
                            const expiryAlert = getExpiryAlert(viewDialog.brand);
                            return expiryAlert && (
                              <Chip
                                icon={<WarningIcon />}
                                label={expiryAlert.message}
                                color={expiryAlert.severity}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            );
                          })()}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Country of Registration</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewDialog.brand.country_of_registration ? 
                            `${viewDialog.brand.country_of_registration} - ${COUNTRY_CODES[viewDialog.brand.country_of_registration] || 'Unknown'}` : 
                            'Not specified'
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Trademark Class</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewDialog.brand.trademark_class || 'Not specified'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Renewal Date</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewDialog.brand.trademark_renewal_date ? 
                            new Date(viewDialog.brand.trademark_renewal_date).toLocaleDateString() : 
                            'Not specified'
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notification Settings</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Notify {viewDialog.brand.auto_notify_days_before_expiry || 90} days before expiry
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Origin Story */}
                  {viewDialog.brand.origin_story && (
                    <Paper sx={{ 
                      p: 3, 
                      mb: 3, 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon /> Origin Story
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        {viewDialog.brand.origin_story}
                      </Typography>
                    </Paper>
                  )}

                  {/* Social Media Links */}
                  {viewDialog.brand.social_media_links && Object.keys(viewDialog.brand.social_media_links).some(key => viewDialog.brand.social_media_links[key]) && (
                    <Paper sx={{ 
                      p: 3, 
                      mb: 3, 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LanguageIcon /> Social Media Links
                      </Typography>
                      <Grid container spacing={2}>
                        {SOCIAL_MEDIA_PLATFORMS.map((platform) => {
                          const IconComponent = platform.icon;
                          const link = viewDialog.brand.social_media_links[platform.key];
                          return link ? (
                            <Grid item xs={12} sm={6} key={platform.key}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 2 }}>
                                <IconComponent sx={{ color: 'primary.main' }} />
                                <Box>
                                  <Typography variant="subtitle2" color="text.secondary">{platform.label}</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{link}</Typography>
                                </Box>
                              </Box>
                            </Grid>
                          ) : null;
                        })}
                      </Grid>
                    </Paper>
                  )}

                  {/* Documents */}
                  {brandDocuments[viewDialog.brand.id] && brandDocuments[viewDialog.brand.id].length > 0 && (
                    <Paper sx={{ 
                      p: 3, 
                      mb: 3, 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon /> Documents ({brandDocuments[viewDialog.brand.id].length})
                      </Typography>
                      <Grid container spacing={2}>
                        {brandDocuments[viewDialog.brand.id].map((doc) => (
                          <Grid item xs={12} sm={6} md={4} key={doc.name}>
                            <Paper 
                              sx={{ 
                                p: 2, 
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  transform: 'translateY(-2px)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <AttachFileIcon sx={{ color: 'primary.main' }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {doc.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(doc.created_at).toLocaleDateString()}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="View Document">
                                    <IconButton 
                                      size="small"
                                      onClick={() => viewBrandDocument(`${viewDialog.brand.id}/${doc.name}`, doc.name)}
                                      sx={{ 
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                                      }}
                                    >
                                      <VisibilityIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download Document">
                                    <IconButton 
                                      size="small"
                                      onClick={() => downloadBrandDocument(`${viewDialog.brand.id}/${doc.name}`, doc.name)}
                                      sx={{ 
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) }
                                      }}
                                    >
                                      <DownloadIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  )}

                  {/* Timestamps */}
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 3, 
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon /> Timestamps
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Created</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(viewDialog.brand.created_at).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Last Updated</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewDialog.brand.updated_at ? 
                            new Date(viewDialog.brand.updated_at).toLocaleString() : 
                            'Not updated'
                          }
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 4, pb: 3, pt: 2, gap: 2 }}>
              {canEdit && (
                <Button 
                  variant="outlined"
                  onClick={() => {
                    handleCloseViewDialog();
                    handleEditBrand(viewDialog.brand);
                  }}
                  startIcon={<EditIcon />}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
                >
                  Edit Brand
                </Button>
              )}
              <Button 
                variant="contained" 
                onClick={handleCloseViewDialog}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Fade>
  );
} 
